import { EventTarget, Font, JsonAsset, sp, SpriteAtlas, SpriteFrame, warn } from 'cc';
import I18nAssetProvider from './I18nAssetProvider';
import SupportLanguage from './SupportLanguage';
import AssetLoadingConfigType from '../Config/AssetLoadingConfigType';
import fallbackLanguages from './FallbackLanguage';
import IPreloader from '../IPreloader';
import ButtonSpriteSet from '../Toolkit/CCClass/ButtonSpriteSet';
import { fillTextArgument } from '../Toolkit/Utils/Utility';
import BundleAssetProvider from '../BundleAssetProvider';

enum I18nEventType {
    LanguageChanged = 'LanguageChanged',
}

type LanguageConfig = {
    initLanguage: string;
    defaultLanguage?: SupportLanguage;
    supportedLanguages?: SupportLanguage[];
};

export default class I18n<T extends I18nAssetProvider = I18nAssetProvider> extends EventTarget implements IPreloader {
    public static readonly EVENT_TYPE = I18nEventType;
    private readonly _assetProvider: T = null;
    private readonly _defaultLanguage: SupportLanguage = SupportLanguage.ENG;
    private readonly _supportedLanguages: SupportLanguage[] = [];
    private _initLanguage: SupportLanguage = null;
    private _lastLanguage: SupportLanguage = null;
    private _waitForLanguageLoadedPromiseMap: { [language in SupportLanguage]?: Promise<void> } = {};
    private _waitForLanguageLoadedResolveMap: { [language in SupportLanguage]?: () => void } = {};

    public constructor(initLanguage: string, assetLoadingConfig: AssetLoadingConfigType);
    public constructor(initLanguage: string, i18nAssetProvider: I18nAssetProvider);
    public constructor(languageConfig: LanguageConfig, assetLoadingConfig: AssetLoadingConfigType);
    public constructor(languageConfig: LanguageConfig, i18nAssetProvider: I18nAssetProvider);
    public constructor(languageConfig: LanguageConfig | string, arg2: AssetLoadingConfigType | I18nAssetProvider) {
        super();
        const {
            initLanguage,
            defaultLanguage = this._defaultLanguage,
            supportedLanguages = SupportLanguage.getAll(),
        } = typeof languageConfig === 'string' ? { initLanguage: languageConfig } : languageConfig;

        this._supportedLanguages = [...supportedLanguages];
        this._defaultLanguage = defaultLanguage;
        this._initLanguage = this.getSupportedLanguage(initLanguage);
        if (arg2 instanceof I18nAssetProvider) {
            this._assetProvider = arg2 as T;
        } else {
            this._assetProvider = new I18nAssetProvider(arg2) as T;
        }
    }

    public get assetProvider() {
        return this._assetProvider;
    }

    public get isLoadingLanguage() {
        return this._assetProvider.isLoadingLanguage;
    }

    public get initLanguage() {
        return this._initLanguage;
    }
    public set initLanguage(value: SupportLanguage) {
        if (this.currentLanguage) {
            warn('Can not change init language after language is loaded');
            return;
        }
        if (this._initLanguage === value) {
            return;
        }
        this._initLanguage = value;
    }

    public get currentLanguage() {
        return this._assetProvider.currentLanguage;
    }

    public get supportedLanguages(): Readonly<SupportLanguage[]> {
        return [...this._supportedLanguages];
    }

    public getSupportedLanguage(language: string): SupportLanguage {
        if (SupportLanguage.contains(language) && this._supportedLanguages.includes(language)) {
            return language;
        }
        return this.getFallbackLanguage(language);
    }

    public defaultLanguage() {
        return this._defaultLanguage;
    }

    public preload() {
        this.changeLanguage(this._initLanguage);
        return this._assetProvider.preload();
    }

    /**
     * 改變當前遊戲語言
     * @param language 要使用的遊戲語言
     */
    public async changeLanguage(language: SupportLanguage) {
        if (!this._supportedLanguages.includes(language)) {
            warn(`Unsupported language: ${language}`);
            return;
        }
        if (language === this.currentLanguage) {
            return;
        }
        await this._assetProvider.loadLanguage(language);
        if (this._waitForLanguageLoadedResolveMap[language]) {
            this._waitForLanguageLoadedResolveMap[language]();
            this._waitForLanguageLoadedResolveMap[language] = null;
            // wait for all addtional loading completed
            await this._waitForLanguageLoaded(language);
        }
        this._lastLanguage = this.currentLanguage;
        this._cacheAllMessagesJson();
        this.emit(I18n.EVENT_TYPE.LanguageChanged);
    }

    public waitForLanguageLoaded() {
        return this._assetProvider.waitForLoaded();
    }

    /**
     * 將要顯示的訊息翻譯成目標語言
     * @param key 要顯示的訊息
     * @param slient 是否在找不到對應的翻譯時不報警告
     * @returns 翻譯後文本
     */
    public getMessage(key: string, slient = false): string {
        const messages = this._getAllMessagesJson().json;
        const resultMessage: string = messages[key];
        if (resultMessage === undefined && !slient) {
            warn(`Can't find message: ${key} in language: ${this.currentLanguage}`);
        }
        return resultMessage;
    }

    public addMessage(messageMap: { [language in SupportLanguage]?: Record<string, string> }): void;
    public addMessage(key: string, messageMap: { [language in SupportLanguage]?: string }): void;
    public addMessage(arg1: string | { [language in SupportLanguage]?: Record<string, string> }, arg2?: { [language in SupportLanguage]?: string }): void {
        this.supportedLanguages.forEach(async (language) => {
            const provider = this._assetProvider.getProvider(language);
            if (!provider) {
                await this._waitForLanguageLoaded(language);
            }
            const allMessagesJson = this._getAllMessagesJson(language).json;
            const messageMap: Record<string, string> = typeof arg1 === 'string' ? { [arg1]: arg2[language] } : arg1[language];
            if (!messageMap) {
                return;
            }
            Object.entries(messageMap).forEach(([key, message]) => {
                if (message) {
                    allMessagesJson[key] = message;
                }
            });
        });
    }

    /**
     * 根據目標圖片取得當前語言中對應的 spriteFrame
     * @param key 目標圖片鍵
     * @param slient 是否在找不到對應的圖片時不報警告
     */
    public getSpriteFrame(key: string, slient = false): SpriteFrame {
        const [spriteAtlasName, spriteFrameName] = key.split('@');
        let resultSpriteFrame: SpriteFrame = null;
        if (spriteFrameName) {
            const spriteAtlas = this._assetProvider.get(spriteAtlasName, SpriteAtlas);
            if (!spriteAtlas) {
                warn(`Can't find spriteAtlas: ${spriteAtlasName} in language: ${this.currentLanguage}`);
            } else {
                resultSpriteFrame = spriteAtlas.getSpriteFrame(spriteFrameName);
            }
        } else {
            resultSpriteFrame = this._assetProvider.get(spriteAtlasName, SpriteFrame);
        }
        if (!resultSpriteFrame && !slient) {
            warn(`Can't find spriteFrame: ${key} in language: ${this.currentLanguage}`);
        }
        return resultSpriteFrame;
    }

    public getButtonSpriteFrameSet(
        key: string,
        pattern: string = '{0}_{1}',
        {
            normal = '01',
            pressed = '03',
            hover = '01',
            disabled = '02',
        }: {
            normal?: string;
            pressed?: string;
            hover?: string;
            disabled?: string;
        } = {
            normal: '01',
            pressed: '03',
            hover: '01',
            disabled: '02',
        },
        slient = false,
    ): ButtonSpriteSet {
        const buttonSpriteFrameSet = new ButtonSpriteSet();
        buttonSpriteFrameSet.normal = this.getSpriteFrame(fillTextArgument(pattern, [key, normal]), slient);
        buttonSpriteFrameSet.pressed = this.getSpriteFrame(fillTextArgument(pattern, [key, pressed]), slient);
        buttonSpriteFrameSet.hover = this.getSpriteFrame(fillTextArgument(pattern, [key, hover]), slient);
        buttonSpriteFrameSet.disabled = this.getSpriteFrame(fillTextArgument(pattern, [key, disabled]), slient);
        return buttonSpriteFrameSet;
    }

    public getSkeletonData(key: string, slient = false): sp.SkeletonData {
        const resultSkeletonData = this._assetProvider.get(key, sp.SkeletonData);
        if (!resultSkeletonData && !slient) {
            warn(`Can't find skeleton data: ${key} in language: ${this.currentLanguage}`);
        }
        return resultSkeletonData;
    }

    public getFont(key: string, slient = false): Font {
        const resultFontAsset = this._assetProvider.get(key, Font);
        if (!resultFontAsset && !slient) {
            warn(`Can't find font asset: ${key} in language: ${this.currentLanguage}`);
        }
        return resultFontAsset;
    }

    /**
     * 取得替代語言
     * @param language 要被替換掉的語言
     */
    public getFallbackLanguage(language: string): SupportLanguage {
        const fallBackLanguage = fallbackLanguages[language];
        if (!fallBackLanguage) {
            return this._defaultLanguage;
        }
        if (SupportLanguage.contains(fallBackLanguage) && this._supportedLanguages.includes(fallBackLanguage)) {
            return fallBackLanguage;
        }
        return this.getFallbackLanguage(fallBackLanguage);
    }

    private _getAllMessagesJson(language?: SupportLanguage): JsonAsset {
        if (this.isLoadingLanguage) {
            language = this._lastLanguage || this._initLanguage;
        }
        const provider = language ? this._assetProvider.getProvider(language) : this._assetProvider;
        let allJsonAsset = provider.get('all', JsonAsset);
        if (!allJsonAsset) {
            allJsonAsset = this._cacheAllMessagesJson(provider);
        }
        return allJsonAsset;
    }

    private _waitForLanguageLoaded(language: SupportLanguage) {
        let promise = this._waitForLanguageLoadedPromiseMap[language];
        if (!promise) {
            promise = new Promise<void>((resolve) => {
                this._waitForLanguageLoadedResolveMap[language] = resolve;
            });
            this._waitForLanguageLoadedPromiseMap[language] = promise;
        }
        return promise;
    }

    private _cacheAllMessagesJson(provider?: BundleAssetProvider): JsonAsset {
        provider = provider || this._assetProvider;
        let allJsonAsset = provider.get('all', JsonAsset);
        if (!allJsonAsset) {
            allJsonAsset = new JsonAsset();
            allJsonAsset.json = {};
            provider.set('all', JsonAsset, allJsonAsset);
        }
        Object.values(provider.getAll(JsonAsset)).forEach((asset) => {
            Object.assign(allJsonAsset.json, asset.json);
        });
        return allJsonAsset;
    }
}
